<template>
  <div class="h-full pb-6">
    <!-- Filters section -->
    <div class="px-4 py-2">
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label for="url-filter" class="block text-sm font-medium text-gray-700">URL</label>
          <input
            type="text"
            id="url-filter"
            v-model="urlFilter"
            @input="applyFilters"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Filter op URL"
          />
        </div>
        <div>
          <label for="user-id-filter" class="block text-sm font-medium text-gray-700">Account</label>
          <input
            type="text"
            id="user-id-filter"
            v-model="userIdFilter"
            @input="applyFilters"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Filter op Account"
          />
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4 mt-2">
        <div>
          <label for="min-pageviews" class="block text-sm font-medium text-gray-700">Min Pageviews</label>
          <input
            type="number"
            id="min-pageviews"
            v-model="minPageviews"
            @input="applyFilters"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Minimum pageviews"
            min="0"
          />
        </div>
        <div>
          <label for="max-pageviews" class="block text-sm font-medium text-gray-700">Max Pageviews</label>
          <input
            type="number"
            id="max-pageviews"
            v-model="maxPageviews"
            @input="applyFilters"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Maximum pageviews"
            min="0"
          />
        </div>
      </div>
    </div>

    <!-- No data message -->
    <div v-if="!dataAvailable && loaded" class="h-full flex justify-center">
      <p class="self-center px-4 -mt-14 text-center">{{ $t('widget.noData') }}</p>
    </div>

    <!-- Data table -->
    <div v-if="dataAvailable && loaded" class="w-full">
      <div class="divide-y divide-gray-300 h-full w-full">
        <table class="divide-y divide-gray-300 w-full mt-2">
          <thead>
          <tr class="flex flex-row px-4">
            <th class="text-left text-l w-3/4 font-semibold text-gray-900">URL</th>
            <th class="text-right text-l w-1/4 font-semibold text-gray-900">Pageviews</th>
          </tr>
          </thead>
          <tbody :class="hasLongTitle ? 'divide-y divide-gray-200 h-28rem flex flex-col overflow-y-auto' : 'divide-y divide-gray-200 flex flex-col overflow-y-auto h-[22rem]'">
            <table-item v-for="item in items"
                        :key="item.url"
                        :name="formatUrl(item.url)"
                        :value="item.pageviews.toString()"
                        :hasInfo="false"
                        :hasScrollBar="items.length > 9"
                        :title="item.url"
            ></table-item>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="!loaded" class="flex h-full items-center justify-center">
      <div class="loader loader--style3">
        <svg id="loader-1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="80px" height="80px" viewBox="0 0 50 50" xml:space="preserve">
          <path class="loadingSpinner h-20" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
            <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/>
          </path>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, onMounted, ref, watch } from "vue";
import TableItem from "@/widgetContent/listItems/TableItem";
import ApiService from "@/services/ApiService";
// import { useApiStore } from "@/stores/ApiStore";

let apiService = new ApiService();
// const apiDataStore = useApiStore();

let props = defineProps({
  update: Number,
  source: String,
  style: Object,
  colorPalette: Object,
  pills: Array,
  type: String,
  hasLongTitle: Boolean
});

const emit = defineEmits(["dataLoaded"]);

let loaded = ref(false);
let items = ref([]);
let dataAvailable = ref(true);
let urlFilter = ref('');
let userIdFilter = ref('');
let minPageviews = ref(null);
let maxPageviews = ref(null);
let debounceTimer = null;

onMounted(async () => {
  await getData();
});

watch(() => props.update, () => {
  getData();
});

function applyFilters() {
  // Debounce the filter application to avoid too many API calls
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    getData();
  }, 500);
}

function formatUrl(url) {
  try {
    // If the URL doesn't start with http or https, return it as is
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return url;
    }

    // Parse the URL to extract the pathname
    const urlObj = new URL(url);
    // Return only the path part (removing domain)
    return urlObj.pathname;
  } catch (e) {
    // If there's an error parsing the URL, return the original
    return url;
  }
}

async function getData() {
  loaded.value = false;
  items.value = [];

  try {
    const response = await apiService.getPageviews(
      urlFilter.value || null,
      userIdFilter.value || null,
      minPageviews.value || null,
      maxPageviews.value || null
    );

    if (response && response.data) {
      items.value = response.data;
      dataAvailable.value = items.value.length > 0;
    } else {
      dataAvailable.value = false;
    }
  } catch (error) {
    console.error("Error fetching pageviews:", error);
    dataAvailable.value = false;
  } finally {
    loaded.value = true;
    emit("dataLoaded");
  }
}
</script>
